import React, { useEffect, useSyncExternalStore } from 'react';
import { Alert, Platform, Pressable, ScrollView, View } from 'react-native';
import { Text } from './Text';
import type { AlertButton } from 'react-native';
import { makeStyles, radius, StyleSheet } from '../theme';
import { Btn, type BtnKind } from './Btn';

/* Web only. react-native-web ships `Alert.alert` as a stub that does nothing, so on the web build every
   confirm, every menu and every error message in the app was silent: "Sign out" did nothing, a failed send
   said nothing. Here `Alert.alert` is pointed at an in-page dialog that takes the same arguments, so the
   screens keep calling `Alert.alert` and need no change.

   Phones keep the native alert: `installWebAlert` returns at its first line on iOS and Android, and the host
   is only mounted on web. */

type Dialog = { id: number; title: string; message?: string; buttons: AlertButton[] };

let queue: Dialog[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const snapshot = () => queue;

function show(title?: string, message?: string, buttons?: AlertButton[]) {
  const list = buttons && buttons.length ? buttons : [{ text: 'OK' }];
  queue = [...queue, { id: ++seq, title: String(title ?? ''), message: message ? String(message) : undefined, buttons: list }];
  emit();
}

/** Close first, then run the button: a button may open the next dialog ("Leave room" asks "Leave room?"). */
function close(d: Dialog, b?: AlertButton) {
  queue = queue.filter((x) => x.id !== d.id);
  emit();
  b?.onPress?.();
}

/** Call once at start-up. Does nothing on phones. */
export function installWebAlert(): void {
  if (Platform.OS !== 'web') return;
  (Alert as unknown as { alert: typeof show }).alert = show;
}

const HOST_ID = 'helm-alert';

/** What Escape and a click outside mean: the Cancel button, or the only button of a plain message. */
const wayOut = (d: Dialog) => d.buttons.find((b) => b.style === 'cancel') ?? (d.buttons.length === 1 ? d.buttons[0] : undefined);

export function WebAlertHost() {
  const s = useStyles();
  const d = useSyncExternalStore(subscribe, snapshot, snapshot)[0];

  useEffect(() => {
    if (!d || typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const out = wayOut(d);
      if (out) { e.preventDefault(); e.stopPropagation(); close(d, out); }
    };
    window.addEventListener('keydown', onKey, true);
    // Keyboard focus goes to the safe choice: Cancel when the other button destroys something, else the action.
    const risky = d.buttons.some((b) => b.style === 'destructive');
    const first = d.buttons.findIndex((b) => (risky ? b.style === 'cancel' : b.style !== 'cancel'));
    const at = first < 0 ? 0 : first;
    const t = setTimeout(() => {
      const el = document.getElementById(`${HOST_ID}-${at}`)?.querySelector<HTMLElement>('[role="button"]');
      el?.focus();
    }, 0);
    return () => { window.removeEventListener('keydown', onKey, true); clearTimeout(t); };
  }, [d]);

  if (!d) return null;
  const menu = d.buttons.length > 2;
  const kind = (b: AlertButton): BtnKind => {
    if (b.style === 'destructive') return 'danger';
    if (b.style === 'cancel') return menu ? 'link' : 'ghost';
    return menu ? 'ghost' : 'primary';
  };
  // Two buttons sit side by side with Cancel on the left, whatever order the screen listed them in.
  const buttons = menu ? d.buttons : [...d.buttons].sort((a, b) => Number(b.style === 'cancel') - Number(a.style === 'cancel'));
  const out = wayOut(d);

  return (
    <View style={s.backdrop} nativeID={HOST_ID}>
      <Pressable style={StyleSheet.absoluteFill} onPress={() => out && close(d, out)} accessibilityLabel="Close" tabIndex={-1} />
      <View style={s.card} role="alertdialog" aria-modal aria-label={d.title}>
        <Text style={s.title}>{d.title}</Text>
        {d.message ? <ScrollView style={s.scroll}><Text style={s.message}>{d.message}</Text></ScrollView> : null}
        <View style={menu ? s.column : s.row}>
          {buttons.map((b, i) => (
            <View key={`${d.id}-${i}`} nativeID={`${HOST_ID}-${d.buttons.indexOf(b)}`} style={menu ? s.wide : undefined}>
              <Btn label={b.text ?? 'OK'} kind={kind(b)} onPress={() => close(d, b)} style={menu ? s.wide : undefined} />
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  backdrop: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 1000, backgroundColor: theme.backdrop, alignItems: 'center', justifyContent: 'center', padding: 16 },
  card: { width: '100%', maxWidth: 400, maxHeight: '80%', backgroundColor: theme.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: theme.border, padding: 20 },
  title: { color: theme.ink, fontSize: 17, fontWeight: '800' },
  scroll: { marginTop: 8, maxHeight: 320 },
  message: { color: theme.muted, fontSize: 14.5, lineHeight: 21 },
  row: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 20 },
  column: { gap: 8, marginTop: 16 },
  wide: { alignSelf: 'stretch' },
}));
